import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Save, Loader2, Lock } from "lucide-react";

const FEATURE_LABELS = {
  map: "Carte / Live",
  history: "Historique & Alertes",
  commands: "Commandes moteur",
  reports: "Rapports & TB",
  invoices: "Factures & Coûts",
  tickets: "Tickets",
  interventions: "Interventions",
  admin: "Administration",
  users: "Utilisateurs",
};

export default function RolesMatrix({ onSaved }) {
  const [roles, setRoles] = useState([]);
  const [features, setFeatures] = useState([]);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(null);

  const load = () => api.get("/roles").then((r) => { setRoles(r.data.roles || []); setFeatures((r.data.features || []).map((f) => f.key)); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = (role, feat) => {
    setRoles((rs) => rs.map((r) => r.role === role ? { ...r, permissions: { ...r.permissions, [feat]: !r.permissions[feat] } } : r));
    setDirty((d) => ({ ...d, [role]: true }));
  };

  const save = async (role) => {
    const r = roles.find((x) => x.role === role);
    if (!r) return;
    setSaving(role);
    try {
      await api.put(`/roles/${role}`, { permissions: r.permissions });
      toast.success(`Rôle « ${r.label} » enregistré`);
      setDirty((d) => ({ ...d, [role]: false }));
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    }
    setSaving(null);
  };

  return (
    <div data-testid="role-matrix">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-5 h-5 brand-teal" />
        <h2 className="text-lg font-bold">Rôles & permissions</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4">Définissez, pour chaque rôle, les fonctionnalités accessibles. Ces cases servent de modèle par défaut quand vous attribuez un rôle à un utilisateur.</p>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary/50 border-b border-border">
              <th className="px-3 py-2.5 text-left font-semibold sticky left-0 bg-secondary/50">Rôle</th>
              {features.map((f) => (
                <th key={f} className="px-2 py-2.5 text-center font-medium text-xs whitespace-nowrap">{FEATURE_LABELS[f] || f}</th>
              ))}
              <th className="px-3 py-2.5 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r) => (
              <tr key={r.role} className="border-t border-border hover:bg-black/5" data-testid={`role-row-${r.role}`}>
                <td className="px-3 py-2.5 font-semibold sticky left-0 bg-card whitespace-nowrap">
                  <span className="flex items-center gap-1.5">{r.locked && <Lock className="w-3.5 h-3.5 brand-orange" />}{r.label}</span>
                </td>
                {features.map((f) => (
                  <td key={f} className="px-2 py-2.5 text-center">
                    <input type="checkbox" data-testid={`role-cell-${r.role}-${f}`}
                      checked={r.locked ? true : !!r.permissions[f]} disabled={r.locked || saving === r.role}
                      onChange={() => toggle(r.role, f)} className="w-4 h-4 accent-[#17A2B8] disabled:opacity-60" />
                  </td>
                ))}
                <td className="px-3 py-2.5 text-center">
                  {r.locked ? (
                    <span className="text-[11px] text-muted-foreground">Tout (verrouillé)</span>
                  ) : (
                    <button onClick={() => save(r.role)} disabled={!dirty[r.role] || saving === r.role} data-testid={`role-save-${r.role}`}
                      className="inline-flex items-center gap-1 rounded-md bg-[#17A2B8] hover:bg-[#17A2B8]/90 text-white text-xs font-semibold px-2.5 py-1.5 transition-colors disabled:opacity-40">
                      {saving === r.role ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
